import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { canAccess, homeFor } from './lib/rbac';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import { ThemeProvider } from './lib/theme';
import AppShell from './components/AppShell';
import Login from './pages/Login';
import PwaStatusBar from './components/PwaStatusBar';
import ImpersonationBanner from './components/ImpersonationBanner';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';

// Route-level code splitting: each page loads its own small chunk on demand
// instead of shipping the whole app (maps, charts, tables) in one bundle.
const ExecutiveDashboard   = lazy(() => import('./pages/ExecutiveDashboard'));
const PaymentRequests      = lazy(() => import('./pages/PaymentRequests'));
const BankAccounts         = lazy(() => import('./pages/BankAccounts'));
// Cash Management disabled: const CashManagement = lazy(() => import('./pages/CashManagement'));
const DailyCollections     = lazy(() => import('./pages/DailyCollections'));
const Vendors              = lazy(() => import('./pages/Vendors'));
const Transactions         = lazy(() => import('./pages/Transactions'));
const Tasks                = lazy(() => import('./pages/Tasks'));
const SalaryData           = lazy(() => import('./pages/SalaryData'));
const Reimbursements       = lazy(() => import('./pages/Reimbursements'));
const OfficeVehicles       = lazy(() => import('./pages/OfficeVehicles'));
const InterEntityTransfers = lazy(() => import('./pages/InterEntityTransfers'));
const Reports              = lazy(() => import('./pages/Reports'));
const Entities             = lazy(() => import('./pages/Entities'));
const EntityProfile        = lazy(() => import('./pages/EntityProfile'));
const Users                = lazy(() => import('./pages/Users'));
const StaffHR              = lazy(() => import('./pages/StaffHR'));
const Directors            = lazy(() => import('./pages/Directors'));
const AuditTrail           = lazy(() => import('./pages/AuditTrail'));
const Backups              = lazy(() => import('./pages/Backups'));
const Records              = lazy(() => import('./pages/Records'));
const StaffPortal          = lazy(() => import('./pages/StaffPortal'));
const WhatsAppApproval     = lazy(() => import('./pages/WhatsAppApproval'));
const DataForms            = lazy(() => import('./pages/DataForms'));
const PublicForm           = lazy(() => import('./pages/PublicForm'));
const StoreDashboard       = lazy(() => import('./pages/StoreDashboard'));
const LiveMap              = lazy(() => import('./pages/LiveMap'));
const DutyRoster           = lazy(() => import('./pages/DutyRoster'));
const SaleCorrections      = lazy(() => import('./pages/SaleCorrections'));
const DataHealth           = lazy(() => import('./pages/DataHealth'));
const Security             = lazy(() => import('./pages/Security'));
const AccessReview         = lazy(() => import('./pages/AccessReview'));
const ActAs                = lazy(() => import('./pages/ActAs'));
const StoreLogin           = lazy(() => import('./pages/store/StoreLogin'));
const StoreApp             = lazy(() => import('./pages/store/StoreApp'));
const OpenCounter          = lazy(() => import('./pages/store/OpenCounter'));
const TvWall               = lazy(() => import('./pages/TvWall'));

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="text-sm text-muted-foreground animate-pulse">Loading…</div>
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace/>;
  if (user.must_change_password) return <ForcePasswordChange/>;
  if (!canAccess(user.role, pathname)) return <Navigate to={homeFor(user.role)} replace/>;
  return <AppShell><ErrorBoundary key={pathname}>{children}</ErrorBoundary></AppShell>;
}

// Store staff get their own bare screen — no command-centre shell around it.
function PortalOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login?mode=staff" replace/>;
  if (user.must_change_password) return <ForcePasswordChange/>;
  if (!canAccess(user.role, '/my')) return <Navigate to={homeFor(user.role)} replace/>;
  return children;
}

// The office wall board: its own bare screen, no command-centre shell.
function WallOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070b0a]">
        <div className="text-sm text-white/50">Loading…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace/>;
  if (!canAccess(user.role, '/tv')) return <Navigate to={homeFor(user.role)} replace/>;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader/>}>
      <Routes>
        <Route path="/login" element={<Login/>}/>
        <Route path="/wa/:token" element={<WhatsAppApproval/>}/>
        <Route path="/f/:slug" element={<PublicForm/>}/>
        <Route path="/store/login" element={<StoreLogin/>}/>
        <Route path="/store/open" element={<OpenCounter/>}/>
        <Route path="/store" element={<StoreApp/>}/>
        <Route path="/tv" element={<WallOnly><TvWall/></WallOnly>}/>
        <Route path="/store-dashboard" element={<Protected><StoreDashboard/></Protected>}/>
        <Route path="/live-map" element={<Protected><LiveMap/></Protected>}/>
        <Route path="/duty-roster" element={<Protected><DutyRoster/></Protected>}/>
        <Route path="/sale-corrections" element={<Protected><SaleCorrections/></Protected>}/>
        <Route path="/data-health" element={<Protected><DataHealth/></Protected>}/>
        <Route path="/security" element={<Protected><Security/></Protected>}/>
        <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
        <Route path="/dashboard" element={<Protected><ExecutiveDashboard/></Protected>}/>
        <Route path="/treasury" element={<Navigate to="/dashboard" replace/>}/>
        <Route path="/payment-requests" element={<Protected><PaymentRequests/></Protected>}/>
        <Route path="/approvals" element={<Navigate to="/payment-requests?filter=verified" replace/>}/>
        <Route path="/bank-accounts" element={<Protected><BankAccounts/></Protected>}/>
        {/* Cash Management disabled per user request */}
        <Route path="/daily-collections" element={<Protected><DailyCollections/></Protected>}/>
        <Route path="/vendors" element={<Protected><Vendors/></Protected>}/>
        <Route path="/transactions" element={<Protected><Transactions/></Protected>}/>
        <Route path="/tasks" element={<Protected><Tasks/></Protected>}/>
        <Route path="/vehicles" element={<Protected><OfficeVehicles/></Protected>}/>
        <Route path="/salary" element={<Protected><SalaryData/></Protected>}/>
        <Route path="/reimbursements" element={<Protected><Reimbursements/></Protected>}/>
        <Route path="/inter-entity" element={<Protected><InterEntityTransfers/></Protected>}/>
        <Route path="/reconciliation" element={<Navigate to="/dashboard" replace/>}/>
        <Route path="/reports" element={<Protected><Reports/></Protected>}/>
        <Route path="/ai-insights" element={<Navigate to="/dashboard" replace/>}/>
        <Route path="/entities" element={<Protected><Entities/></Protected>}/>
        <Route path="/entities/:id" element={<Protected><EntityProfile/></Protected>}/>
        <Route path="/staff-hr" element={<Protected><StaffHR/></Protected>}/>
        <Route path="/directors" element={<Protected><Directors/></Protected>}/>
        <Route path="/users" element={<Protected><Users/></Protected>}/>
        <Route path="/access-review" element={<Protected><AccessReview/></Protected>}/>
        <Route path="/act-as" element={<ActAs/>}/>
        <Route path="/audit" element={<Protected><AuditTrail/></Protected>}/>
        <Route path="/backups" element={<Protected><Backups/></Protected>}/>
        <Route path="/records" element={<Protected><Records/></Protected>}/>
        <Route path="/my" element={<PortalOnly><StaffPortal/></PortalOnly>}/>
        <Route path="/forms" element={<Protected><DataForms/></Protected>}/>
        <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes/>
          <ImpersonationBanner/>
          <PwaStatusBar/>
          <Toaster position="top-right" theme="system"/>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
